import React from 'react';
import { cx } from '../components/styles';
import { CONTAINER, SECTION_PADDING, SHOW_SOCIAL_PROOF } from './landingConfig';
import { Reveal } from './Reveal';

/**
 * Section 6 — social proof (DESIGN_PLAN §1.6).
 *
 * BUILT BUT NOT SHIPPED. `SHOW_SOCIAL_PROOF` is `false`, so this returns `null`
 * and section 5 flows into section 7 with no visible gap.
 *
 * Proof must be named and specific. There are no customer names, no
 * testimonials and no verified metrics available, and inventing them is a legal
 * and reputational problem, not a placeholder. The arrays below are empty on
 * purpose: fill them from the table in DESIGN_PLAN §1.6, with permission to
 * publish and with a measurement date on every figure, then flip the flag.
 *
 * Rules for whoever fills this in: state the figure AND its date
 * (`1.2M tickets synced · as of July 2026`). Round down. Never write a number
 * you cannot re-derive from a query.
 */

interface Quote {
  /** One sentence, max 22 words. */
  quote: string;
  /** Name, then city or what they sell. */
  attribution: string;
}

interface Figure {
  /** The number, rounded down. */
  value: string;
  /** One word. */
  label: string;
  /** When it was measured, e.g. `as of July 2026`. */
  measuredAt: string;
}

const QUOTES: Quote[] = [];
const FIGURES: Figure[] = [];

/**
 * Fallback if no quotes ever land. ⚠️ Only use this if it is actually true —
 * confirm before shipping (DESIGN_PLAN open question 8).
 */
const FALLBACK_LINE = 'Built by ticket sellers, for ticket sellers.';
const FALLBACK_LINE_CONFIRMED = false;

function monogram(attribution: string): string {
  const name = attribution.split(',')[0]?.trim() ?? '';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export const ProofSection: React.FC = () => {
  if (!SHOW_SOCIAL_PROOF) return null;

  return (
    <Reveal as="section" className={SECTION_PADDING}>
      <div className={CONTAINER}>
        <h2 className="text-3xl font-bold tracking-tight text-white">
          Sellers who stopped switching tabs.
        </h2>

        {QUOTES.length > 0 ? (
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {QUOTES.map((item) => (
              <figure
                key={item.attribution}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
              >
                <blockquote className="text-sm leading-relaxed text-white/80">
                  “{item.quote}”
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-xs font-semibold text-white/60">
                    {monogram(item.attribution)}
                  </span>
                  <span className="text-xs text-white/50">— {item.attribution}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          FALLBACK_LINE_CONFIRMED && (
            <p className="mt-8 text-lg text-white/60">{FALLBACK_LINE}</p>
          )
        )}

        {FIGURES.length > 0 && (
          <div className={cx('mt-12 grid grid-cols-3 gap-6 border-t border-white/[0.06] pt-10')}>
            {FIGURES.map((figure) => (
              <div key={figure.label}>
                <p className="text-3xl font-bold tabular-nums text-white">{figure.value}</p>
                <p className="mt-1 text-sm text-white/60">{figure.label}</p>
                <p className="mt-0.5 text-xs text-white/35">{figure.measuredAt}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Reveal>
  );
};
